import { hasRfxSubmissionDeadlinePassed } from "components/RFQ/SubmissionDeadline/SubmissionDeadline";
import {
	rfxSubmission,
	RfxSubmissionStatus,
} from "newui/features/Rfx/Submission";
import { Rfx, RfxApprover } from "newui/features/Rfx/types/rfx.types";
import { RFX_TABS } from "utils/constants";

// TASK-036 (typecheck sweep): widened to Partial — bidder card rows carry a
// slim rfq shape; only `rfxApprovers` is read, and it is already null-guarded.
export const isRfxApproved = (
	rfx: Pick<Partial<Rfx>, "rfxApprovers"> | undefined
): boolean => {
	if (!rfx) {
		return false;
	}

	if (!rfx?.rfxApprovers || rfx?.rfxApprovers?.length === 0) {
		return true;
	}

	const approvedBy = getRfxApprover(rfx);
	return approvedBy !== undefined;
};

export const doesSubmissionStatusMatch = (
	submissionStatus: string,
	matchTo: RfxSubmissionStatus | RfxSubmissionStatus[]
): boolean => {
	return Array.isArray(matchTo)
		? matchTo.includes(submissionStatus?.toUpperCase())
		: matchTo === submissionStatus.toUpperCase();
};

export const hasInviteeAcceptedRfx = (submissionStatus: string): boolean => {
	return doesSubmissionStatusMatch(
		submissionStatus,
		rfxSubmission.constants.HAS_ACCEPTED
	);
};

export const hasInviteeSubmittedRfx = (submissionStatus: string): boolean => {
	return doesSubmissionStatusMatch(
		submissionStatus,
		rfxSubmission.constants.HAS_SUBMITTED
	);
};

export const getRfxApprover = (
	rfx: Pick<Partial<Rfx>, "rfxApprovers"> | undefined
): RfxApprover | null => {
	return rfx?.rfxApprovers?.find((rfxApprover) => {
		return rfxApprover.hasApproved === true;
	});
};

export const doesRFxHaveApprovers = (rfx: Rfx): boolean => {
	return rfx?.rfxApprovers?.length > 0;
};

export const canViewCurrentTab = (currentTabId: string) => {
	const allowedTabsAfterClose = [
		RFX_TABS.DOCUMENTATION,
		RFX_TABS.COMMS,
		RFX_TABS.ADDENDUMS,
		RFX_TABS.CLARIFICATIONS,
	];
	return allowedTabsAfterClose.includes(currentTabId?.toLowerCase());
};

export const isBeingRevised = (status: string): boolean => {
	return doesSubmissionStatusMatch(
		status,
		rfxSubmission.constants.RFX_SUBMISSION_STATUS.BEING_REVISED
	);
};

/**
 * Statuses in which a bidder's response may be auto-saved.
 *
 * BEING_REVISED is excluded: once a revision is under way the only sanctioned
 * write path is the revision pipeline (draft cells via the quote-revision
 * endpoints, then an explicit submit). The server enforces this — saveSubmission
 * rejects post-deadline writes via failIfDeadlinePassed, and the revision
 * endpoints accept BEING_REVISED alone — so autosaving here produces a write the
 * server refuses, surfaced to the bidder as a spurious validation error.
 */
const AUTOSAVE_ALLOWED_STATUSES = [
	rfxSubmission.constants.RFX_SUBMISSION_STATUS.ACCEPTED,
	rfxSubmission.constants.RFX_SUBMISSION_STATUS.INPROGRESS,
];

/**
 * `isClientView` is the procurer-viewing-a-bidder flag. It is required rather
 * than optional so that no call site can gate autosave on status alone: a
 * procurer must never trigger a write against a bidder's submission on any tab.
 */
export const isAutosaveAllowed = (
	status: string | undefined | null,
	isClientView: boolean
): boolean => {
	if (!status || isClientView) {
		return false;
	}

	return AUTOSAVE_ALLOWED_STATUSES.includes(status.toUpperCase());
};

export const canEditRfxResponse = (
	isLoading: boolean,
	rfqDetail: Rfx,
	submissionStatus: string
): boolean => {
	if (isLoading) {
		return false;
	}

	const hasDeadlinePassed = hasRfxSubmissionDeadlinePassed(rfqDetail);
	const hasAccepted = hasInviteeAcceptedRfx(submissionStatus);
	const hasSubmitted = hasInviteeSubmittedRfx(submissionStatus);

	if (!hasDeadlinePassed && hasAccepted && !hasSubmitted) {
		return true;
	}

	if (hasDeadlinePassed || hasSubmitted) {
		return false;
	}

	return false;
};
